import { push } from '@lagunovsky/redux-react-router'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import HomeAction from 'home/actions/HomeAction'
import { omit, orderBy } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { SIM_ID } from 'materiel/constants/MaterielConstants'
import { H_MAT_SIM } from '../../../../account/constants/AccessRulesConstants'
import FilePanel from '../../../../components/file/FilePanel'
import SmallPicturePanel from '../../../../components/picture/SmallPicturePanel'
import UpdatePanel from '../../../../components/updatePanel/UpdatePanel'
import { getDate } from '../../../../utils/DateUtil'
import { componentHasHabilitations } from '../../../../utils/HabilitationUtil'
import { updateMaterialize } from '../../../../utils/MaterializeUtils'
import { getUser } from '../../../../utils/SettingUtils'
import { isSaveBlocked } from '../../../utils/MaterielUtils'
import AdministratorPanel from '../../administrator/AdministratorPanel'
import AssigmentChartPanel from '../../chart/AssigmentChartPanel'
import LastSituationMarkerPanel from '../../situation/LastSituationMarkerPanel'
import SituationPanel from '../../situation/SituationPanel'
import SubscriptionAction from '../../subscription/actions/SubscriptionAction'
import SimSubscriptionTable from '../../subscription/components/SimSubscriptionTable'
import SimAction from '../actions/SimAction'
import SimDto from '../dto/SimDto'
import SimPanel from './SimPanel'

const SimApp = () => {
    const {
        sim: storeSim,
        sims,
        simsLastSituations,
        simTypes,
        simSituations,
        simPictures,
        simFiles,
        subscriptionsLink,
        subscriptions,
        subscriptionTypes,
        materielSettingRules,
    } = useSelector(store => ({
        sim: store.SimReducer.sim,
        sims: store.SimReducer.sims,
        simsLastSituations: store.SimReducer.simsLastSituations,
        simTypes: store.SimReducer.simTypes,
        simSituations: store.SimReducer.simSituations,
        simPictures: store.SimReducer.simPictures,
        simFiles: store.SimReducer.simFiles,
        subscriptionsLink: store.SimReducer.subscriptionsLink,
        subscriptions: store.SubscriptionReducer.subscriptions,
        subscriptionTypes: store.SubscriptionReducer.subscriptionTypes,
        materielSettingRules: store.MaterielReducer.materielSettingRules,
    }))

    const dispatch = useDispatch()
    const params = useParams()

    const [sim, setSim] = useState({})
    const [isEditMode, setIsEditMode] = useState(false)

    const fetchSim = id => dispatch(SimAction.fetchSim(id)).then(json => {
        if (json.id) {
            dispatch(SimAction.fetchSimSituations(json.id))
            dispatch(SimAction.fetchSubscriptionsLink(json.id))
            dispatch(SimAction.fetchSimPictures(json.id))
            dispatch(SimAction.fetchSimFiles(json.serialNumber))
            setSim(new SimDto(json))
        }
    })

    const changeAdministrator = administrator => setSim(prev => ({ ...prev, administrator }))

    const changeStateAttribute = changes => setSim(prev => ({ ...prev, ...changes }))

    const deleteSituation = id => dispatch(SimAction.deleteSimSituation(id, params.id))

    const filterConsultant = actions => getUser().consultant === '1'
        ? omit(actions, ['save', 'edit', 'delete', 'replace'])
        : actions

    useActions(() => {
        updateMaterialize()
        if (params.id === 'new' && !storeSim.id) {
            return filterConsultant({
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === SIM_ID)
                    if (isSaveBlocked(sims, sim, rules, 'simType')) return

                    dispatch(SimAction.createSim(sim)).then(id => {
                        if (id) {
                            const situation = {
                                idSim: id,
                                situationDate: sim.purchaseDate,
                                statusCode: 2,
                            }
                            dispatch(SimAction.saveSimSituation(situation))
                        }
                    })
                },
                cancel: () => {
                    dispatch(push('/materiel/sim'))
                },
            })
        }
        if (isEditMode) {
            return filterConsultant({
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === SIM_ID)
                    if (isSaveBlocked(sims, sim, rules, 'simType')) return

                    dispatch(SimAction.updateSim(sim)).then(() => setIsEditMode(false))
                },
                cancel: () => {
                    setSim({ ...storeSim })
                    setIsEditMode(false)
                },
            })
        }
        return filterConsultant({
            edit: () => {
                setIsEditMode(true)
            },
            delete: () => {
                dispatch(SimAction.deleteSim(params.id))
            },
            replace: () => {
                dispatch(ToastrAction.info(i18n.inDeveloppmentFunctionnality))
            },
        })
    }, [isEditMode, sim, storeSim, sims, materielSettingRules])

    useTitle(() => {
        const { reference = '', serialNumber = '', simType, id } = storeSim
        const title = reference || serialNumber || id
        const type = (simTypes.find(t => t.code === parseInt(simType)) || {}).label || i18n.notDefined
        return ([
            {
                title: i18n.materiels,
                href: 'materiel',
            },
            {
                title: i18n.sims,
                href: 'materiel/sim',
            },
            {
                title: type,
                href: `materiel/sim/${params.id}`,
            },
            {
                title,
                href: `materiel/sim/${params.id}`,
            },
        ])
    }, [storeSim, simTypes])

    useEffect(() => {
        if (!componentHasHabilitations(H_MAT_SIM)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
            return undefined
        }
        if (!sims.length) {
            dispatch(SimAction.fetchSims(true))
        }
        if (!simTypes.length) {
            dispatch(SimAction.fetchSimTypes())
        }
        if (!subscriptions.length) {
            dispatch(SubscriptionAction.fetchSubscriptions())
        }
        if (!subscriptionTypes.length) {
            dispatch(SubscriptionAction.fetchSubscriptionTypes())
        }
        dispatch(HomeAction.setHelpLink('materiel', ''))
        return () => dispatch(SimAction.resetSim())
    }, [])

    useEffect(() => {
        if (params.id !== 'new') {
            fetchSim(params.id)
            setIsEditMode(false)
        } else {
            setIsEditMode(true)
        }
    }, [params.id])

    const getMaterielNumber = ({ serialNumber, reference }) => serialNumber || reference || ''

    const getSimLabel = s => {
        const number = getMaterielNumber(s)
        if (s.simType) {
            const type = simTypes.find(t => t.id === s.simType)
            if (type) {
                return `${type.label} - ${number}`
            }
        }
        return number
    }

    const getSimsFormat = () => sims
        .filter(({ isEquipment }) => !isEquipment)
        .map(s => ({
            id: s.id,
            label: getSimLabel(s),
        }))

    const disabled = { active: isEditMode, disabled: !isEditMode }

    const formatedLinks = orderBy(subscriptionsLink, [({ startDate }) => startDate ? startDate : 0, ({ endDate }) => endDate ? endDate : 0], ['desc', 'desc']).map(({ idSim, idSubscription, startDate, endDate, comment }) => {
        const subscription = subscriptions.find(({ id }) => idSubscription === id)
        if (subscription) {
            const type = subscriptionTypes.find(({ id }) => id === subscription.subscriptionType)
            return {
                subscription: `${type ? `${type.name}:` : ''} ${subscription.numLine || ''}`,
                startDate: getDate(startDate),
                endDate: getDate(endDate),
                comment,
                idSim,
                idSubscription,
            }
        }
        return {
            startDate: getDate(startDate),
            endDate: getDate(endDate),
            comment,
            idSim,
            idSubscription,
        }
    })

    const formatedSubscriptions = subscriptions.map(({ numLine = '', id, subscriptionType }) => {
        const type = subscriptionTypes.find(({ code }) => code === subscriptionType)
        return {
            code: id,
            name: `${type ? `${type.name}:` : ''} ${numLine}`,
        }
    })

    return (
        <Card>
            <CardContent>
                <Grid container size={12} spacing={2}>
                    <Grid container size={9}>
                        <LastSituationMarkerPanel materielSituation={simSituations} />
                        <Grid container size={'grow'}>
                            <Grid size={12}>
                                <SimPanel
                                    sim={sim}
                                    onChange={changeStateAttribute}
                                    {...disabled}
                                />
                            </Grid>
                            <Grid size={12}>
                                <SimSubscriptionTable
                                    links={formatedLinks}
                                    editMode={isEditMode}
                                    headers={['subscription', 'startDate', 'endDate', 'comment']}
                                    title={i18n.subscriptions}
                                    associatedMateriels={formatedSubscriptions}
                                    onDelete={({ idSim, idSubscription }) => {
                                        dispatch(SubscriptionAction.deleteSimSubscriptionLink(idSim, idSubscription)).then(() => dispatch(SimAction.fetchSubscriptionsLink(params.id)))
                                    }}
                                    idKeyToRedirect={'idSubscription'}
                                    redirectTo={'subscription'}
                                    onAdd={({ materielLink, startDate, endDate, comment }) => {
                                        const simSubscription = {
                                            idSubscription: materielLink,
                                            idSim: storeSim.id,
                                            startDate,
                                            endDate,
                                            comment,
                                        }
                                        dispatch(SimAction.createSimSubscription(simSubscription)).then(() =>
                                            dispatch(SimAction.fetchSubscriptionsLink(params.id)))
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={3}>
                        <Stack spacing={1}>
                            <UpdatePanel
                                updateDate={storeSim.updateDate}
                                updateLogin={storeSim.updateLogin}
                                jobExecId={storeSim.jobExecutionId}
                            />
                            <Card>
                                <CardContent>
                                    <AdministratorPanel
                                        onChange={changeAdministrator}
                                        selectedValue={sim.administrator}
                                        isActive={!sim.isEquipment && isEditMode}
                                    />
                                </CardContent>
                            </Card>
                            <AssigmentChartPanel materielSituation={simSituations} />
                            <SmallPicturePanel pictures={simPictures} />
                            <FilePanel files={simFiles} />
                        </Stack>
                    </Grid>
                    {
                        !sim.isEquipment && (
                            <Grid size={12}>
                                <SituationPanel
                                    materielSituation={simSituations}
                                    active={isEditMode}
                                    idMateriel={storeSim.id}
                                    serialNumber={storeSim.serialNumber}
                                    purchaseDate={storeSim.purchaseDate}
                                    idKey={'idSim'}
                                    lastSituations={simsLastSituations}
                                    materials={getSimsFormat()}
                                    saveFunction={situation => dispatch(SimAction.saveSimSituation(situation))}
                                    deleteFunction={deleteSituation}
                                />
                            </Grid>
                        )
                    }
                </Grid>
            </CardContent>
        </Card>
    )
}

export default SimApp
